import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  getSession,
  maybeRecordOAuthLogin,
  onAuthStateChange,
  sessionsEqual,
  type Session,
} from '@pc/db';

// Same shape as every other app's AuthProvider, with one twist: the
// auth site doesn't own a "post-sign-in destination" -- that's always
// the return_to target supplied by the caller. So the context here is
// intentionally minimal.

interface AuthCtx {
  session: Session | null;
  loading: boolean;
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    getSession()
      .then((s) => {
        if (alive) setSession(s);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    const off = onAuthStateChange((event, s) => {
      // Avoid resetting downstream route-gate state on auth-js no-op
      // SIGNED_IN re-emits (e.g. visibilitychange).
      setSession((prev) => (sessionsEqual(prev, s) ? prev : s));
      // A Google sign-in lands back here via supabase-js's
      // detectSessionInUrl, which fires SIGNED_IN but bypasses the
      // signInWithPassword audit writer. maybeRecordOAuthLogin self-gates
      // on the `pc.oauth.login_pending` marker so this is a no-op for
      // password sessions and token refreshes.
      if (event === 'SIGNED_IN') {
        void maybeRecordOAuthLogin(s);
      }
    });
    return () => {
      alive = false;
      off();
    };
  }, []);

  const value = useMemo<AuthCtx>(() => ({ session, loading }), [session, loading]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth must be used inside <AuthProvider>.');
  return v;
}
